import type { SchedulerEventSide, SchedulerResourceId } from '@mui/x-scheduler-internals/models';
import type {
  SchedulerDependency,
  SchedulerDependencyId,
} from '@mui/x-scheduler-internals-premium/models';
import { getDependencyEdges } from '@mui/x-scheduler-internals-premium/internals';
import type {
  DependencyAnchorResolver,
  DependencyArrowEndpoint,
  DependencyArrowObstacle,
  DependencyArrowPoint,
} from './dependencyAnchorResolver';
import {
  DEPENDENCY_ARROW_CORNER_RADIUS,
  buildDependencyArrowRoutes,
  buildOffRangeDependencyArrowRoute,
  buildRoundedOrthogonalPath,
  countRouteCollisions,
} from './dependencyArrowRouting';
import {
  DEPENDENCY_ARROW_HIT_TRIM_END,
  DEPENDENCY_ARROW_HIT_TRIM_START,
  clipRouteAroundObstacles,
  getArrowHeadHitArea,
  trimRouteEnds,
} from './dependencyArrowHitArea';

export interface DependencyArrow {
  /**
   * Unique key of the arrow: a dependency renders one arrow per pair of rows of its
   * events.
   */
  key: string;
  id: SchedulerDependencyId;
  /**
   * The resources of the rows the arrow leaves and enters.
   */
  sourceResourceId: SchedulerResourceId;
  targetResourceId: SchedulerResourceId;
  /**
   * The SVG path of the arrow, in absolute row-space pixels (y = 0 is the top of the
   * first row), so it does not depend on the scroll position.
   * Built on first read and cached, like `hitD`: a row re-measure recomputes every
   * arrow, and only the ones surviving the viewport filter need their string.
   */
  readonly d: string;
  /**
   * The path of the invisible click hit-area: the same route with both ends trimmed,
   * so the terminal (at the source anchor) and the resize handle (under the
   * arrowhead) stay reachable.
   * Derived on first read and cached: only the interactions layer needs it.
   */
  readonly hitD: string;
  /**
   * The click hit-area of the arrowhead, next to `endPoint` on the side the arrow comes
   * from and cut before any event box, or `null` when an event leaves no room for it.
   * Derived on first read and cached, like `hitD`.
   */
  readonly hitHead: { x: number; width: number } | null;
  /**
   * The point where the arrowhead is drawn, in the same coordinate space as `d`: the
   * target edge anchor, except when the route clamps at the timeline edge or the target
   * is outside the visible range.
   */
  endPoint: DependencyArrowPoint;
  /**
   * The edge of the target event the arrow enters, and so the side of `endPoint` the
   * arrow comes from. For a target outside the visible range, the edge the arrow would
   * enter coming from inside the timeline.
   */
  targetEdge: SchedulerEventSide;
  /**
   * Horizontal bounding box of the arrow, as fractions of the events area width.
   */
  minXFraction: number;
  maxXFraction: number;
  /**
   * Vertical bounding box of the arrow, as row indexes.
   */
  minRowIndex: number;
  maxRowIndex: number;
}

/**
 * Computes the arrow of each renderable dependency, connecting the edges of its two
 * events that its type constrains. When only one event is in the visible range, the
 * other is reached through the timeline edge on its side.
 */
export function computeDependencyArrows(
  resolver: DependencyAnchorResolver,
  dependencies: readonly SchedulerDependency[],
): DependencyArrow[] {
  const { eventsWidth } = resolver;

  if (dependencies.length === 0 || eventsWidth <= 0) {
    return [];
  }

  const buildArrow = (
    dependency: SchedulerDependency,
    sourceAnchor: DependencyArrowEndpoint,
    targetAnchor: DependencyArrowEndpoint,
  ): DependencyArrow | null => {
    if (
      !resolver.hasRowPosition(sourceAnchor.rowIndex) ||
      !resolver.hasRowPosition(targetAnchor.rowIndex)
    ) {
      return null;
    }

    const minRowIndex = Math.min(sourceAnchor.rowIndex, targetAnchor.rowIndex);
    const maxRowIndex = Math.max(sourceAnchor.rowIndex, targetAnchor.rowIndex);
    const edges = getDependencyEdges(dependency.type);
    const source = resolver.getEdgePoint(sourceAnchor, edges.source);
    const target = resolver.getEdgePoint(targetAnchor, edges.target);

    // The event boxes the route may cross, used to pick the route and to cut the
    // hit-area around them. The endpoint events stay out: the end trims already
    // handle their edges. Gathered on demand — a single-candidate route only needs
    // them if something reads the hit-area.
    let obstacles: DependencyArrowObstacle[] | null = null;
    const getObstacles = () => {
      if (obstacles === null) {
        const gathered: DependencyArrowObstacle[] = [];
        for (let rowIndex = minRowIndex; rowIndex <= maxRowIndex; rowIndex += 1) {
          for (const obstacle of resolver.getRowObstacles(rowIndex)) {
            if (
              obstacle.occurrenceKey !== sourceAnchor.occurrence?.key &&
              obstacle.occurrenceKey !== targetAnchor.occurrence?.key
            ) {
              gathered.push(obstacle);
            }
          }
        }
        obstacles = gathered;
      }
      return obstacles;
    };

    const pickRoute = (): DependencyArrowPoint[] | null => {
      if (sourceAnchor.occurrence === null || targetAnchor.occurrence === null) {
        return buildOffRangeDependencyArrowRoute(
          source,
          target,
          edges,
          targetAnchor.occurrence === null ? 'target' : 'source',
          resolver.detourOffset,
          eventsWidth,
        );
      }
      const routes = buildDependencyArrowRoutes(
        source,
        target,
        dependency.type,
        resolver.detourOffset,
        eventsWidth,
      );
      // With several candidates, keep the one crossing the fewest events (first wins on
      // a tie). Best-effort avoidance, not full pathfinding.
      let route = routes[0];
      if (routes.length > 1) {
        const routeObstacles = getObstacles();
        let bestCollisions = countRouteCollisions(route, routeObstacles);
        for (let index = 1; index < routes.length && bestCollisions > 0; index += 1) {
          const collisions = countRouteCollisions(routes[index], routeObstacles);
          if (collisions < bestCollisions) {
            bestCollisions = collisions;
            route = routes[index];
          }
        }
      }
      return route;
    };

    const points = pickRoute();
    if (points === null) {
      return null;
    }
    // The arrowhead of a target outside the range points out of the timeline: after the
    // range, it comes from the left like into a start edge.
    let targetEdge: SchedulerEventSide = edges.target;
    if (targetAnchor.occurrence === null) {
      targetEdge = targetAnchor.side === 'after' ? 'start' : 'end';
    }

    let minX = Infinity;
    let maxX = -Infinity;
    for (const point of points) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
    }

    let d: string | null = null;
    let hitD: string | null = null;
    let hitHead: DependencyArrow['hitHead'] | undefined;
    const endPoint = points[points.length - 1];

    return {
      // The id type is part of the key: `SchedulerDependencyId` accepts both strings
      // and numbers, so `1` and `"1"` would otherwise share a key on the same row pair.
      key: `${typeof dependency.id}:${String(dependency.id)}:${sourceAnchor.rowIndex}:${targetAnchor.rowIndex}`,
      id: dependency.id,
      sourceResourceId: sourceAnchor.resourceId,
      targetResourceId: targetAnchor.resourceId,
      get d() {
        if (d === null) {
          d = buildRoundedOrthogonalPath(points, DEPENDENCY_ARROW_CORNER_RADIUS);
        }
        return d;
      },
      get hitD() {
        if (hitD === null) {
          hitD = clipRouteAroundObstacles(
            trimRouteEnds(points, DEPENDENCY_ARROW_HIT_TRIM_START, DEPENDENCY_ARROW_HIT_TRIM_END),
            getObstacles(),
          )
            .map((polyline) => buildRoundedOrthogonalPath(polyline, DEPENDENCY_ARROW_CORNER_RADIUS))
            .filter((subpath) => subpath !== '')
            .join(' ');
        }
        return hitD;
      },
      get hitHead() {
        if (hitHead === undefined) {
          // Unlike `getObstacles`, the source stays in: a back-to-back predecessor sits
          // right under the arrowhead.
          hitHead = getArrowHeadHitArea(
            endPoint,
            targetEdge === 'start' ? -1 : 1,
            resolver
              .getRowObstacles(targetAnchor.rowIndex)
              .filter((obstacle) => obstacle.occurrenceKey !== targetAnchor.occurrence?.key),
          );
        }
        return hitHead;
      },
      endPoint,
      targetEdge,
      minXFraction: minX / eventsWidth,
      maxXFraction: maxX / eventsWidth,
      minRowIndex,
      maxRowIndex,
    };
  };

  const arrows: DependencyArrow[] = [];
  for (const dependency of dependencies) {
    const sourceAppearances = resolver.getAppearances(dependency.source);
    const targetAppearances = resolver.getAppearances(dependency.target);

    // With neither event in the visible range, nothing would explain the arrow.
    if (sourceAppearances.length === 0 && targetAppearances.length === 0) {
      continue;
    }

    const sourceAnchors: readonly DependencyArrowEndpoint[] =
      sourceAppearances.length > 0
        ? sourceAppearances
        : resolver.getOffRangeAnchors(dependency.source);
    const targetAnchors: readonly DependencyArrowEndpoint[] =
      targetAppearances.length > 0
        ? targetAppearances
        : resolver.getOffRangeAnchors(dependency.target);

    // An endpoint without an anchor is not rendered in the timeline: its event is not
    // loaded, has no resource, its row is hidden, or the hour window hides it between two
    // visible days. The dependency stays in the data, it just has no arrow.
    if (sourceAnchors.length === 0 || targetAnchors.length === 0) {
      continue;
    }

    // One arrow per pair of appearances: an event assigned to several resources shows
    // the dependency in each of its rows.
    for (const sourceAnchor of sourceAnchors) {
      for (const targetAnchor of targetAnchors) {
        const arrow = buildArrow(dependency, sourceAnchor, targetAnchor);
        if (arrow != null) {
          arrows.push(arrow);
        }
      }
    }
  }

  return arrows;
}
